import { useAuthStore } from "@/store/auth.store"
import { getAnonymousFingerprint } from "@/lib/copilot/fingerprint"
import { CHAT_API_BASE, ChatError } from "./chat.api"
import type {
  ApprovalRequiredData,
  RunEventFrame,
  ToolActivity,
  ToolEventData,
  ToolResultRequiredData,
} from "./chat.types"

// ── SSE wire parsing ────────────────────────────────────────────────────────────

export interface SseEvent {
  id?: string
  event?: string
  data?: string
}

/**
 * Parse a `text/event-stream` body into discrete SSE events.
 * Handles the standard wire format: `id:`, `event:`, `data:` lines, comment
 * lines (`:`), blank-line dispatch, and multi-line `data:` accumulation.
 */
export async function* readSseEvents(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<SseEvent> {
  const reader = stream.getReader()
  const decoder = new TextDecoder("utf-8")
  let buffer = ""
  let current: SseEvent | null = null

  const dispatch = (): SseEvent | null => {
    if (!current) return null
    const event = current
    current = null
    return event
  }

  /**
   * Process a set of completed lines (split on \n). Blank lines dispatch the
   * accumulated event; when `flushAtEnd` is true a trailing partial event is
   * dispatched once the stream closes.
   */
  const processLines = (completed: string[], flushAtEnd: boolean): SseEvent[] => {
    const events: SseEvent[] = []
    for (const raw of completed) {
      const line = raw.replace(/\r$/, "")
      if (line === "") {
        const event = dispatch()
        if (event) events.push(event)
        continue
      }
      if (line.startsWith(":")) continue

      if (line.startsWith("id:")) {
        current = current ?? {}
        current.id = line.slice(3).trim()
        continue
      }
      if (line.startsWith("event:")) {
        current = current ?? {}
        current.event = line.slice(6).trim()
        continue
      }
      if (line.startsWith("data:")) {
        current = current ?? {}
        const value = line.slice(5).trimStart()
        current.data = current.data ? `${current.data}\n${value}` : value
        continue
      }
      // Unsupported field — ignore per SSE spec (e.g. retry:)
    }
    if (flushAtEnd) {
      const event = dispatch()
      if (event) events.push(event)
    }
    return events
  }

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel().catch(() => undefined)
        break
      }
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""

      for (const event of processLines(lines, false)) yield event

      if (done) {
        if (buffer !== "") {
          for (const event of processLines([buffer], true)) yield event
          buffer = ""
        }
        break
      }
    }
  } finally {
    const trailing = dispatch()
    if (trailing) yield trailing
  }
}

export interface StreamRunHandlers {
  onDelta?: (content: string) => void
  onReasoning?: (content: string) => void
  onTool?: (tool: ToolActivity) => void
  onApproval?: (approval: ApprovalRequiredData) => void
  /** A frontend tool (e.g. get_page_context) wants the client to compute + POST its result. */
  onToolResultRequired?: (data: ToolResultRequiredData) => void
  onDone?: (final?: { messageId?: string }) => void
  onError?: (err: Error) => void
}

export interface StreamRunOptions {
  signal?: AbortSignal
  seen?: Set<number>
}

function normalizeTool(data: ToolEventData): Omit<ToolActivity, "status"> {
  return {
    id: data.id ?? data.toolName ?? data.name ?? "tool",
    name: data.name ?? data.toolName ?? "tool",
    arguments: data.arguments,
  }
}

/**
 * Stream a run's events over SSE using fetch + ReadableStream.
 *
 * The server replays persisted events on (re)connect, so callers MUST dedupe by
 * `frame.sequence`. A `seen` set is kept here and returned to the caller so the
 * same set can be passed across reconnect attempts.
 *
 * The stream terminates on `done` / `run.completed` / `run.failed` /
 * `run.cancelled`, or when the fetching `AbortController` is aborted.
 */
export async function streamRun(
  runId: string,
  handlers: StreamRunHandlers = {},
  options: StreamRunOptions = {}
): Promise<void> {
  const token = useAuthStore.getState().accessToken
  const params = new URLSearchParams()
  if (token) params.set("token", token)
  else {
    const fingerprint = getAnonymousFingerprint()
    if (fingerprint) params.set("fingerprint", fingerprint)
  }
  const url = `${CHAT_API_BASE}/runs/${runId}/events?${params.toString()}`

  let closed = false
  const finish = (fn?: () => void) => {
    if (closed) return
    closed = true
    fn?.()
  }

  const seen = options.seen ?? new Set<number>()

  try {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options.signal,
    })

    if (!res.ok) {
      throw new ChatError(res.status, `SSE connection failed with status ${res.status}`)
    }
    if (!res.body) {
      throw new Error("SSE response body is not readable")
    }

    for await (const ev of readSseEvents(res.body, options.signal)) {
      if (closed) break

      // Named `done` event — terminal, closes the stream.
      if (ev.event === "done") {
        finish(() => handlers.onDone?.())
        break
      }
      if (ev.data == null) continue

      let frame: RunEventFrame
      try {
        frame = JSON.parse(ev.data) as RunEventFrame
      } catch {
        continue
      }

      // Replay-safety: skip duplicates by sequence. Persisted events carry a
      // sequence and arrive both replayed and live; live-only frames
      // (text.delta / reasoning) carry no sequence and are never replayed, so
      // they must always pass through.
      if (typeof frame.sequence === "number") {
        if (seen.has(frame.sequence)) continue
        seen.add(frame.sequence)
      }

      switch (frame.type) {
        case "text.delta": {
          const content = (frame.data as { content?: string })?.content
          if (content) handlers.onDelta?.(content)
          break
        }
        case "reasoning": {
          const content = (frame.data as { content?: string })?.content
          if (content) handlers.onReasoning?.(content)
          break
        }
        case "tool.started":
          handlers.onTool?.({ ...normalizeTool(frame.data as ToolEventData), status: "running" })
          break
        case "tool.completed":
          handlers.onTool?.({ ...normalizeTool(frame.data as ToolEventData), status: "completed" })
          break
        case "approval.required":
          handlers.onApproval?.(frame.data as ApprovalRequiredData)
          break
        case "tool.result_required":
          handlers.onToolResultRequired?.(frame.data as ToolResultRequiredData)
          break
        case "run.completed":
          finish(() => handlers.onDone?.(frame.data as { messageId?: string }))
          break
        case "run.failed":
          finish(() =>
            handlers.onError?.(new Error((frame.data as { error?: string })?.error ?? "Run failed"))
          )
          break
        case "run.cancelled":
          finish()
          break
        default:
          break
      }
    }

    finish()
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return
    handlers.onError?.(err instanceof Error ? err : new Error(String(err)))
  }
}